import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withTranslations } from 'react-utilities';
import { createSystemFeedback, Button, Loading } from 'react-style-guide';
import translationConfig from '../translation.config';
import GameInstance from '../containers/GameInstance';
import gameInstanceConstants from '../constants/serverListConstants';

const [SystemFeedback, systemFeedbackService] = createSystemFeedback();
const { slowGameFpsThreshold, resources } = gameInstanceConstants;

function GameSection({
  translate,
  headerTitle,
  type,
  placeId,
  showLoadMoreButton,
  gameInstances,
  loadMoreGameInstances,
  refreshGameInstances,
  handleGameInstanceShutdownAtIndex,
  userCanManagePlace,
  loadingError,
  isLoading,
  setIsLoading
}) {
  const cssKey = type ? `${type}-` : '';
  const id = `rbx-${cssKey}running-games`;
  const emptyGameInstanceList = gameInstances.length === 0;
  const itemContainerId = `rbx-${cssKey}game-server-item-container`;
  const itemContainerClass = `section stack-list rbx-${cssKey}game-server-item-container`;
  const footerClass = `rbx-${cssKey}running-games-footer`;
  const gameInstanceList = gameInstances.map(
    (
      {
        id: instanceId,
        playing,
        maxPlayers,
        pfs,
        vipServerId,
        vipServerSubscription,
        accessCode,
        name,
        owner,
        players
      },
      index
    ) => (
      <GameInstance
        key={id}
        {...{
          id: instanceId,
          translate,
          cssKey,
          placeId,
          canManagePlace: userCanManagePlace,
          name,
          vipServerId,
          vipServerSubscription,
          owner,
          accessCode,
          showSlowGameMessage: pfs < slowGameFpsThreshold,
          gameServerStatus: translate(resources.playerCountText, {
            currentPlayers: playing || players.length,
            maximumAllowedPlayers: maxPlayers
          }),
          players,
          onShutdownServerSuccess: () => {
            handleGameInstanceShutdownAtIndex(index);
          },
          systemFeedbackService,
          isLoading,
          setIsLoading
        }}
      />
    )
  );

  return (
    <Fragment>
      <SystemFeedback />
      <div id={id} className='stack' data-placeid={placeId} data-showshutdown>
        {headerTitle && (
          <div className='container-header'>
            <h3>{headerTitle}</h3>
            <Button
              size={Button.sizes.extraSmall}
              variant={Button.variants.control}
              className='btn-more rbx-refresh refresh-link-icon'
              onClick={refreshGameInstances}
              isDisabled={isLoading}>
              {translate(resources.privateServerRefreshText) || 'Refresh'}
            </Button>
          </div>
        )}

        {emptyGameInstanceList ? (
          <div className='section-content-off empty-game-instances-container'>
            {isLoading ? (
              <Loading />
            ) : (
              <p className='no-servers-message'>
                {loadingError
                  ? translate(resources.loadServersError) || 'Unable to load servers.'
                  : translate(resources.noServersFoundText)}
              </p>
            )}
          </div>
        ) : (
          <Fragment>
            <ul id={itemContainerId} className={itemContainerClass}>
              {gameInstanceList}
            </ul>
            <div className={footerClass}>
              {showLoadMoreButton && (
                <Button
                  type='button'
                  onClick={() => loadMoreGameInstances()}
                  className='rbx-running-games-load-more'
                  variant={Button.variants.control}
                  width={Button.widths.full}
                  isDisabled={isLoading}>
                  {translate(resources.loadMoreButtonText)}
                </Button>
              )}
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}

GameSection.defaultProps = {
  type: '',
  showLoadMoreButton: false,
  loadMoreButtonText: '',
  headerTitle: '',
  gameInstances: []
};

GameSection.propTypes = {
  translate: PropTypes.func.isRequired,
  type: PropTypes.string,
  placeId: PropTypes.number.isRequired,
  headerTitle: PropTypes.string,
  loadMoreGameInstances: PropTypes.func.isRequired,
  showLoadMoreButton: PropTypes.bool,
  loadMoreButtonText: PropTypes.string,
  gameInstances: PropTypes.arrayOf(PropTypes.any),
  refreshGameInstances: PropTypes.func.isRequired,
  handleGameInstanceShutdownAtIndex: PropTypes.func.isRequired,
  userCanManagePlace: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  loadingError: PropTypes.bool.isRequired
};

export default withTranslations(GameSection, translationConfig.serverList);
